/**
 * Unified Date Formatting & Parsing Utilities for Mộc Phát ERP.
 *
 * Principles:
 * 1. Default display format is dd/mm for the current year, dd/mm/yy for other years.
 * 2. Full date format is dd/mm/yyyy (useful for tooltips / titles).
 * 3. Pure integer calculation for dates without timezone drift.
 */

export interface DateParts {
  year: number
  month: number // 1-12
  day: number // 1-31
  hour?: number // 0-23
  minute?: number // 0-59
  second?: number // 0-59
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function getDaysInMonth(year: number, month: number): number {
  if (month < 1 || month > 12) return 0
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28
  }
  if (month === 4 || month === 6 || month === 9 || month === 11) {
    return 30
  }
  return 31
}

export function isValidCalendarDate(year: number, month: number, day: number): boolean {
  if (year < 1000 || year > 9999) return false
  if (month < 1 || month > 12) return false
  if (day < 1) return false
  return day <= getDaysInMonth(year, month)
}

/**
 * Extracts date and optional time parts safely without unwanted UTC/local shifts for pure date strings.
 */
export function extractDateParts(
  x: string | number | Date | null | undefined,
): DateParts | null {
  if (x == null || x === '') return null

  if (typeof x === 'string') {
    const trimmed = x.trim()
    // Pure date: YYYY-MM-DD
    const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    if (dateOnlyMatch) {
      const year = parseInt(dateOnlyMatch[1], 10)
      const month = parseInt(dateOnlyMatch[2], 10)
      const day = parseInt(dateOnlyMatch[3], 10)
      if (isValidCalendarDate(year, month, day)) {
        return { year, month, day, hour: 0, minute: 0, second: 0 }
      }
      return null
    }

    // ISO timestamp with optional time: YYYY-MM-DD[T ]HH:mm...
    const isoMatch = trimmed.match(
      /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.\d+)?)?)?/,
    )
    if (isoMatch && !trimmed.endsWith('Z') && !/[+-]\d{2}(?::?\d{2})?$/.test(trimmed)) {
      // Local ISO string without timezone offset
      const year = parseInt(isoMatch[1], 10)
      const month = parseInt(isoMatch[2], 10)
      const day = parseInt(isoMatch[3], 10)
      const hour = isoMatch[4] ? parseInt(isoMatch[4], 10) : 0
      const minute = isoMatch[5] ? parseInt(isoMatch[5], 10) : 0
      const second = isoMatch[6] ? parseInt(isoMatch[6], 10) : 0
      if (isValidCalendarDate(year, month, day)) {
        return { year, month, day, hour, minute, second }
      }
      return null
    }

    // Has timezone offset (e.g. Z or +07:00), parse as Date object
    const d = new Date(trimmed)
    if (isNaN(d.getTime())) return null
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
      second: d.getSeconds(),
    }
  }

  const d = x instanceof Date ? x : new Date(x)
  if (isNaN(d.getTime())) return null
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
    second: d.getSeconds(),
  }
}

/**
 * Formats a date into 'dd/mm' if in the same year as currentYear, or 'dd/mm/yy' if in another year.
 * Returns empty string if invalid or null.
 */
export function formatDate(
  x: string | number | Date | null | undefined,
  currentYear?: number,
): string {
  const parts = extractDateParts(x)
  if (!parts) return ''

  const baseYear = currentYear ?? new Date().getFullYear()
  const dd = String(parts.day).padStart(2, '0')
  const mm = String(parts.month).padStart(2, '0')

  if (parts.year === baseYear) {
    return `${dd}/${mm}`
  }
  const yy = String(parts.year % 100).padStart(2, '0')
  return `${dd}/${mm}/${yy}`
}

/**
 * Formats a date into 'dd/mm/yyyy'. Suitable for tooltips and full date views.
 */
export function formatDateFull(
  x: string | number | Date | null | undefined,
): string {
  const parts = extractDateParts(x)
  if (!parts) return ''

  const dd = String(parts.day).padStart(2, '0')
  const mm = String(parts.month).padStart(2, '0')
  const yyyy = String(parts.year)
  return `${dd}/${mm}/${yyyy}`
}

/**
 * Formats a timestamp into 'dd/mm HH:mm' if same year, or 'dd/mm/yy HH:mm' if different year.
 */
export function formatDateTime(
  x: string | number | Date | null | undefined,
  currentYear?: number,
): string {
  const parts = extractDateParts(x)
  if (!parts) return ''

  const baseYear = currentYear ?? new Date().getFullYear()
  const dd = String(parts.day).padStart(2, '0')
  const mm = String(parts.month).padStart(2, '0')
  const HH = String(parts.hour ?? 0).padStart(2, '0')
  const MM = String(parts.minute ?? 0).padStart(2, '0')

  if (parts.year === baseYear) {
    return `${dd}/${mm} ${HH}:${MM}`
  }
  const yy = String(parts.year % 100).padStart(2, '0')
  return `${dd}/${mm}/${yy} ${HH}:${MM}`
}

/**
 * Formats a timestamp into 'dd/mm/yyyy HH:mm'. Suitable for tooltips.
 */
export function formatDateTimeFull(
  x: string | number | Date | null | undefined,
): string {
  const parts = extractDateParts(x)
  if (!parts) return ''

  const dd = String(parts.day).padStart(2, '0')
  const mm = String(parts.month).padStart(2, '0')
  const yyyy = String(parts.year)
  const HH = String(parts.hour ?? 0).padStart(2, '0')
  const MM = String(parts.minute ?? 0).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${HH}:${MM}`
}

export type TodayReference = Date | { year: number; month: number; day: number }

/**
 * Parses user hand-typed date string into ISO date string 'YYYY-MM-DD'.
 * Supports:
 * - 'YYYY-MM-DD'
 * - 'd/m', 'dd/mm' (picks closest valid year in [todayYear-1, todayYear, todayYear+1])
 * - 'd/m/yy', 'dd/mm/yy'
 * - 'd/m/yyyy', 'dd/mm/yyyy'
 * Supported delimiters: '/', '-', '.'
 *
 * Returns 'YYYY-MM-DD' or null if invalid or impossible calendar date.
 */
export function parseDateInput(
  text: string,
  today?: TodayReference,
): string | null {
  if (!text) return null
  const trimmed = text.trim()
  if (!trimmed) return null

  // 1. Direct ISO match: YYYY-MM-DD
  const isoMatch = trimmed.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/)
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10)
    const month = parseInt(isoMatch[2], 10)
    const day = parseInt(isoMatch[3], 10)
    if (isValidCalendarDate(year, month, day)) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }
    return null
  }

  // 2. Format: d/m, dd/mm, d/m/yy, dd/mm/yyyy with delimiters / - .
  const dmyMatch = trimmed.match(/^(\d{1,2})[-/.](\d{1,2})(?:[-/.](\d{2}|\d{4}))?$/)
  if (!dmyMatch) return null

  const day = parseInt(dmyMatch[1], 10)
  const month = parseInt(dmyMatch[2], 10)
  const rawYear = dmyMatch[3]

  if (month < 1 || month > 12 || day < 1 || day > 31) return null

  let finalYear: number

  if (rawYear) {
    if (rawYear.length === 2) {
      finalYear = 2000 + parseInt(rawYear, 10)
    } else {
      finalYear = parseInt(rawYear, 10)
    }
    if (!isValidCalendarDate(finalYear, month, day)) {
      return null
    }
  } else {
    // Year omitted. Determine today's Y, M, D
    let tY: number
    let tM: number
    let tD: number
    if (today instanceof Date) {
      tY = today.getFullYear()
      tM = today.getMonth() + 1
      tD = today.getDate()
    } else if (today && typeof today === 'object') {
      tY = today.year
      tM = today.month
      tD = today.day
    } else {
      const now = new Date()
      tY = now.getFullYear()
      tM = now.getMonth() + 1
      tD = now.getDate()
    }

    // Candidate years: [tY - 1, tY, tY + 1]
    const candidates = [tY - 1, tY, tY + 1].filter((y) =>
      isValidCalendarDate(y, month, day),
    )
    if (candidates.length === 0) {
      return null
    }

    // Find candidate with minimum distance in days to today
    const targetUtc = Date.UTC(tY, tM - 1, tD)
    let bestYear = candidates[0]
    let minDiff = Infinity

    for (const cand of candidates) {
      const candUtc = Date.UTC(cand, month - 1, day)
      const diff = Math.abs(candUtc - targetUtc)
      if (diff < minDiff) {
        minDiff = diff
        bestYear = cand
      } else if (diff === minDiff && cand === tY) {
        // Prefer current year in case of ties
        bestYear = cand
      }
    }

    finalYear = bestYear
  }

  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${finalYear}-${mm}-${dd}`
}
